import { useCallback, useEffect, useState } from 'react';

import { request } from '../lib/api';
import { ProtectedResourcePolicyControls } from './ProtectedResourcePolicyControls';

type AccessRequest = {
  id: string;
  requesterEmail: string;
  status: string;
  verifiedAt: string | null;
  decidedAt: string | null;
  decisionReason: string | null;
  requestExpiresAt: string;
  createdAt: string;
  resource: { id: string; fileName: string; state: string };
  grant: null | {
    status: string;
    expiresAt: string;
    consumedAt: string | null;
    revokedAt: string | null;
    revocable: boolean;
  };
};

type AccessResponse = { requests: AccessRequest[]; nextCursor: string | null };
type ApiFailure = { status?: number; data?: { message?: string } };

const statusLabel = (value: string) => value.toLowerCase().replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase());

export function ProtectedResourceAccessPanel() {
  const [pending, setPending] = useState<AccessRequest[]>([]);
  const [history, setHistory] = useState<AccessRequest[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingResponse, historyResponse] = await Promise.all([
        request<AccessResponse>('/me/protected-resources/requests?view=pending&take=20'),
        request<AccessResponse>('/me/protected-resources/requests?view=history&take=10')
      ]);
      setPending(pendingResponse.requests);
      setHistory(historyResponse.requests);
      setAvailable(true);
    } catch (error) {
      const failure = error as ApiFailure;
      if (failure.status === 403) {
        setAvailable(false);
      } else {
        setMessage(failure.data?.message ?? 'Protected file requests could not be loaded.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(async (entry: AccessRequest, decision: 'approve' | 'deny') => {
    setBusyId(entry.id);
    setMessage(null);
    try {
      await request(`/me/protected-resources/requests/${encodeURIComponent(entry.id)}/decision`, {
        method: 'POST',
        body: JSON.stringify({ decision })
      });
      setMessage(decision === 'approve'
        ? 'Access approved for one download within five minutes.'
        : 'Access request denied.');
      await load();
    } catch (error) {
      const failure = error as ApiFailure;
      setMessage(failure.data?.message ?? 'The request could not be updated.');
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const revoke = useCallback(async (entry: AccessRequest) => {
    setBusyId(entry.id);
    setMessage(null);
    try {
      await request(`/me/protected-resources/requests/${encodeURIComponent(entry.id)}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Revoked by the resource owner.' })
      });
      setMessage('The unused download permission was revoked.');
      await load();
    } catch (error) {
      const failure = error as ApiFailure;
      setMessage(failure.data?.message ?? 'The permission could not be revoked.');
    } finally {
      setBusyId(null);
    }
  }, [load]);

  if (!available) return null;

  return (
    <section className="protected-access-panel" aria-labelledby="protected-access-title">
      <header className="protected-access-panel__header">
        <div>
          <span className="portal-eyebrow">Protected files</span>
          <h2 id="protected-access-title">Access requests</h2>
          <p>Verified visitors wait for your decision. Approval permits one download for five minutes.</p>
        </div>
        <button type="button" className="button-secondary" onClick={() => void load()} disabled={loading}>Refresh</button>
      </header>

      {message ? <div className="alert" role="status">{message}</div> : null}
      {loading ? <p className="helper-text">Loading protected file requests…</p> : null}

      {!loading && pending.length === 0 ? (
        <div className="protected-access-panel__empty">
          <strong>No requests need review</strong>
          <span>New verified requests will appear here.</span>
        </div>
      ) : null}

      {pending.length > 0 ? (
        <ul className="protected-access-list">
          {pending.map((entry) => (
            <li key={entry.id}>
              <div>
                <strong>{entry.resource.fileName}</strong>
                <span>{entry.requesterEmail}</span>
                <small>Verified {entry.verifiedAt ? new Date(entry.verifiedAt).toLocaleString() : 'recently'}</small>
              </div>
              <div className="protected-access-list__actions">
                <button type="button" className="btn-primary" disabled={busyId === entry.id} onClick={() => void decide(entry, 'approve')}>Approve</button>
                <button type="button" className="button-secondary" disabled={busyId === entry.id} onClick={() => void decide(entry, 'deny')}>Deny</button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {history.length > 0 ? (
        <details className="protected-access-history">
          <summary>Recent access history</summary>
          <ul className="protected-access-list protected-access-list--history">
            {history.map((entry) => (
              <li key={`${entry.id}-${entry.status}`}>
                <div>
                  <strong>{entry.resource.fileName}</strong>
                  <span>{entry.requesterEmail}</span>
                  <small>{statusLabel(entry.grant?.status ?? entry.status)} · {new Date(entry.createdAt).toLocaleString()}</small>
                </div>
                {entry.grant?.revocable ? (
                  <button type="button" className="button-secondary" disabled={busyId === entry.id} onClick={() => void revoke(entry)}>Revoke</button>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      <ProtectedResourcePolicyControls />
    </section>
  );
}
